import React, { useState, useEffect, useCallback } from 'react';
import { customers, bikes } from '../services/api';
import Pagination from './Pagination';

const MasterCustomers = ({ user }) => {
  const [customerList, setCustomerList] = useState([]);
  const [bikeList, setBikeList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Filter and pagination states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBikeFilter, setSelectedBikeFilter] = useState('');
  const [pagination, setPagination] = useState({
    total: 0,
    last_page: 1,
    per_page: 50,
    current_page: 1,
    from: 0,
    to: 0
  });

  // Form states
  const [formData, setFormData] = useState({
    customer_name: '',
    vehicle_number: '',
    engine_number: '',
    customer_phone_number: '',
    customer_assembly_year: '',
    bike_id: '',
    last_km: '0'
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch bikes on mount
  useEffect(() => {
    fetchBikes();
  }, []);

  const fetchBikes = async () => {
    try {
      const res = await bikes.getAll();
      setBikeList(res.data.data || []);
    } catch (err) {
      console.error('Failed to fetch bikes', err);
    }
  };

  // Fetch customers with search, bike filter, and page
  const fetchCustomers = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: 50,
        search: searchTerm,
        bike_id: selectedBikeFilter
      };
      const res = await customers.getAll(params);
      setCustomerList(res.data.data || []);
      setPagination(res.data.pagination || {
        total: (res.data.data || []).length,
        last_page: 1,
        per_page: 50,
        current_page: 1,
        from: 1,
        to: (res.data.data || []).length
      });
    } catch (err) {
      console.error('Failed to fetch customers', err);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedBikeFilter]);

  // Fetch customers whenever search or bike filter changes or on initial mount
  useEffect(() => {
    fetchCustomers(1);
  }, [fetchCustomers]);

  const handleOpenModal = (item = null) => {
    setError('');
    setSuccess('');
    
    if (item) {
      setEditingItem(item);
      setFormData({
        customer_name: item.customer_name || '',
        vehicle_number: item.vehicle_number || '',
        engine_number: item.engine_number || '',
        customer_phone_number: item.customer_phone_number || '',
        customer_assembly_year: item.customer_assembly_year || '',
        bike_id: item.bike_id || '',
        last_km: item.last_km || '0'
      });
    } else {
      setEditingItem(null);
      setFormData({
        customer_name: '',
        vehicle_number: '',
        engine_number: '',
        customer_phone_number: '',
        customer_assembly_year: '',
        bike_id: '',
        last_km: '0'
      });
    }
    setShowModal(true);
  };

  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSaving(true);
    
    // Clean up empty fields to null for backend
    const payload = {
      customer_name: formData.customer_name,
      vehicle_number: formData.vehicle_number,
      engine_number: formData.engine_number || null,
      customer_phone_number: formData.customer_phone_number,
      customer_assembly_year: formData.customer_assembly_year ? parseInt(formData.customer_assembly_year) : null,
      bike_id: formData.bike_id ? parseInt(formData.bike_id) : null,
      last_km: formData.last_km ? parseInt(formData.last_km) : 0
    };

    try {
      if (editingItem) {
        await customers.update(editingItem.id, payload);
        setSuccess('Pelanggan berhasil diperbarui.');
      } else {
        await customers.create(payload);
        setSuccess('Pelanggan baru berhasil ditambahkan.');
      }
      
      setTimeout(() => {
        setShowModal(false);
        fetchCustomers(pagination.current_page);
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal menyimpan data pelanggan.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus data pelanggan ini?')) return;
    setDeletingId(id);
    try {
      await customers.delete(id);
      fetchCustomers(pagination.current_page);
    } catch (err) {
      alert(err.response?.data?.message || 'Gagal menghapus pelanggan.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Master Pelanggan</h2>
          <p className="text-gray-500 text-sm mt-1">Kelola data kendaraan dan kontak pelanggan.</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="bg-blue-600 text-white px-5 py-2.5 rounded-lg text-sm font-bold hover:bg-blue-700 transition shadow-md shadow-blue-200 self-start md:self-auto"
        >
          + Tambah Pelanggan
        </button>
      </div>

      {/* Filters & Search */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="w-full md:w-72 relative">
          <input
            type="text"
            placeholder="Cari nama, plat, no. mesin, no. HP..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm"
          />
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>

        <div className="w-full md:w-auto flex gap-4 items-center">
          <select
            value={selectedBikeFilter}
            onChange={(e) => setSelectedBikeFilter(e.target.value)}
            className="w-full md:w-56 px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm bg-white"
          >
            <option value="">Semua Tipe Motor</option>
            {bikeList.map((bike) => (
              <option key={bike.id} value={bike.id}>
                {bike.name} ({bike.code})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Customer Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Nama Pelanggan</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Nomor Plat</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Nomor Mesin</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">No. Telepon</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Tipe Motor</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Tahun Perakitan</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Last KM</th>
                <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Aksi</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-gray-500 font-medium">Memuat data pelanggan...</td>
                </tr>
              ) : customerList.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-gray-500 font-medium">Tidak ada data pelanggan ditemukan.</td>
                </tr>
              ) : (
                customerList.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 transition duration-150">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-gray-900">{item.customer_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-1 rounded text-xs font-bold bg-gray-100 text-gray-800 border border-gray-200">
                        {item.vehicle_number || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-mono text-gray-600">{item.engine_number || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">{item.customer_phone_number}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {item.bike ? (
                        <div className="text-sm font-semibold text-gray-900">
                          {item.bike.name} <span className="text-xs text-gray-500">({item.bike.code})</span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 font-medium">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">{item.customer_assembly_year || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-blue-600">{(item.last_km || 0).toLocaleString()} Km</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleOpenModal(item)}
                        className="text-blue-600 hover:text-blue-900 bg-blue-50 px-3 py-1.5 rounded-lg mr-2 transition"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="text-red-600 hover:text-red-900 bg-red-50 px-3 py-1.5 rounded-lg transition"
                      >
                        Hapus
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && customerList.length > 0 && (
          <Pagination
            pagination={pagination}
            onPageChange={(page) => fetchCustomers(page)}
          />
        )}
      </div>

      {/* Modal Form */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full overflow-hidden animate-in fade-in zoom-in duration-200 my-8">
            <div className="p-6 border-b bg-gray-50 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold text-gray-900">{editingItem ? 'Edit Pelanggan' : 'Tambah Pelanggan Baru'}</h3>
                <p className="text-sm text-gray-500 mt-1">Isi formulir lengkap untuk mengelola data motor pelanggan.</p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSave} className="p-6 space-y-4">
              {error && (
                <div className="bg-red-50 text-red-600 p-3.5 rounded-xl text-sm font-medium border border-red-100 flex gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span>{error}</span>
                </div>
              )}

              {success && (
                <div className="bg-green-50 text-green-600 p-3.5 rounded-xl text-sm font-medium border border-green-100 flex gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{success}</span>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Nama Pelanggan *</label>
                  <input
                    type="text"
                    required
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                    placeholder="Contoh: Budi Santoso"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Nomor Telepon (WhatsApp) *</label>
                  <input
                    type="text"
                    required
                    value={formData.customer_phone_number}
                    onChange={(e) => setFormData({ ...formData, customer_phone_number: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                    placeholder="Contoh: 08123456789"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Nomor Plat (Nopol) *</label>
                  <input
                    type="text"
                    required
                    value={formData.vehicle_number}
                    onChange={(e) => setFormData({ ...formData, vehicle_number: e.target.value.toUpperCase() })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                    placeholder="Contoh: B 1234 ABC"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Nomor Mesin</label>
                  <input
                    type="text"
                    value={formData.engine_number}
                    onChange={(e) => setFormData({ ...formData, engine_number: e.target.value.toUpperCase() })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm font-mono"
                    placeholder="JM31E..."
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">
                    Nomor WhatsApp / HP <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.customer_phone_number}
                    onChange={(e) => setFormData({ ...formData, customer_phone_number: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                    placeholder="081234567890"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Tahun Perakitan</label>
                  <input
                    type="number"
                    value={formData.customer_assembly_year}
                    onChange={(e) => setFormData({ ...formData, customer_assembly_year: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                    placeholder="2022"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Tipe Motor</label>
                  <select
                    value={formData.bike_id}
                    onChange={(e) => setFormData({ ...formData, bike_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm bg-white"
                  >
                    <option value="">Pilih Tipe Motor...</option>
                    {bikeList.map((bike) => (
                      <option key={bike.id} value={bike.id}>
                        {bike.name} ({bike.code})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Last KM</label>
                  <input
                    type="number"
                    value={formData.last_km}
                    onChange={(e) => setFormData({ ...formData, last_km: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-8 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  disabled={isSaving}
                  className="flex-1 py-2.5 bg-gray-100 text-gray-700 font-bold rounded-xl hover:bg-gray-200 transition"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="flex-1 py-2.5 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition shadow-md shadow-blue-200 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSaving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Menyimpan...</span>
                    </>
                  ) : (
                    'Simpan Data'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterCustomers;
